import {
  AidModelCalculationRequestCalculationGiftTargetType,
  AidModelCalculationRequestGrantCap,
  CalculationRuleRequestAwardLogic,
  EligibilityCriteriaDtoCriteria,
  EligibilityCriteriaDtoOperand,
} from 'api/generated/schemas';

import type {
  CalculationGiftTargetType,
  CalculationGrantCap,
  CalculationSectionVisibility,
  CalculationVariant,
  SpecialAwardAppliesTo,
  SpecialAwardCriteria,
} from './calculations-step.types';

interface CalculationOption<TValue> {
  value: TValue;
  label: string;
}

export const CALCULATION_VARIANTS = {
  percentOfNeed: 'percentOfNeed',
  agiSai: 'agiSai',
  flat: 'flat',
} as const satisfies Record<CalculationVariant, CalculationVariant>;

export const DEFAULT_GIFT_TARGET: CalculationGiftTargetType =
  AidModelCalculationRequestCalculationGiftTargetType.COA;

export const DEFAULT_GRANT_CAP: CalculationGrantCap =
  AidModelCalculationRequestGrantCap.NO_GRANT_CAP;

export const GIFT_TARGET_OPTIONS = [
  {
    value: AidModelCalculationRequestCalculationGiftTargetType.COA,
    label: 'Cost of Attendance',
  },
  {
    value: AidModelCalculationRequestCalculationGiftTargetType.DIRECT,
    label: 'Direct cost',
  },
  {
    value: AidModelCalculationRequestCalculationGiftTargetType.TUITION,
    label: '% of Tuition',
  },
] as const satisfies ReadonlyArray<
  CalculationOption<CalculationGiftTargetType>
>;

export const GRANT_CAP_OPTIONS = [
  {
    value: AidModelCalculationRequestGrantCap.NO_GRANT_CAP,
    label: 'No Grant Cap used',
  },
  {
    value: AidModelCalculationRequestGrantCap.INSTITUTIONAL,
    label: 'Institutional Grant Cap',
  },
  {
    value: AidModelCalculationRequestGrantCap.TOTAL_GRANT_CAP,
    label: 'Total Grant Cap (all sources)',
  },
] as const satisfies ReadonlyArray<CalculationOption<CalculationGrantCap>>;

/** Dictionary type for the special-award awarding-logic dropdown (slice 3). */
export const AWARDING_LOGIC_DICTIONARY_TYPE = 'AWARDING_TYPE';

/**
 * Awarding amount and logic are required by the backend (`CalculationRuleRequest`).
 * A newly created special award defaults to this amount; the awarding logic
 * defaults to the first dictionary entry (`AWARDING_TYPE` is server-sorted, so
 * this is `STACK_CAPPED`) — see `AddSpecialAwardModal`.
 */
export const DEFAULT_SPECIAL_AWARD_AMOUNT = 0;

/**
 * Fallback awarding logic for a persisted rule that predates this required-field
 * validation (`ruleToRequest`) — mirrors the backend's `AwardingType.SORTED_TYPES`
 * first entry so it agrees with the dictionary-driven creation default above.
 */
export const DEFAULT_AWARDING_LOGIC =
  CalculationRuleRequestAwardLogic.STACK_CAPPED;

/** Dictionary type backing the gift-target radios. */
export const GIFT_TARGET_DICTIONARY_TYPE = 'CALCULATION_GIFT_TARGET_TYPE';

/** Dictionary type backing the grant-cap radios. */
export const GRANT_CAP_DICTIONARY_TYPE = 'GRANT_CAP';

export const SPECIAL_AWARD_ADDED_TOAST = {
  title: 'Special award has been added',
  message: 'Now you can configure its amount and logic',
};

/**
 * Copy for the edit flow (ADO #36652 § 3.0). The story only specifies the
 * "added" toast, so the "updated" wording mirrors it.
 */
export const SPECIAL_AWARD_UPDATED_TOAST = {
  title: 'Special award has been updated',
  message: 'Now you can configure its amount and logic',
};

/** Fallback copy when a save fails without a `detail` from the backend. */
export const SPECIAL_AWARD_SAVE_ERROR_TOAST = {
  title: 'Error saving special award',
  message: 'An error occurred',
};

/** Predefined populations, one checkbox each (FR-4). */
export const SPECIAL_AWARD_PRESETS: ReadonlyArray<{
  key: keyof SpecialAwardAppliesTo;
  label: string;
}> = [
  { key: 'campusVisit', label: 'Campus visitors' },
  { key: 'pellGrant', label: 'Pell Grant recipients' },
  { key: 'fafsaGrant', label: 'FAFSA filers' },
];

/** "Criteria" dropdown on each criteria block, no default. */
export const SPECIAL_AWARD_CRITERIA_OPTIONS = [
  { value: 'STUDENT_DATA', label: 'Student data' },
  { value: 'AWARDING_REGIMES', label: 'Awarding regimes' },
  { value: 'ELIGIBILITY', label: 'Eligibility' },
] as const satisfies ReadonlyArray<CalculationOption<SpecialAwardCriteria>>;

/** Dictionary type backing the Eligibility "Dimension" dropdown. */
export const ELIGIBILITY_CRITERIA_DICTIONARY_TYPE = 'ELIGIBILITY_CRITERIA_TYPE';

/** Dictionary type backing the Eligibility "Condition" dropdown. */
export const ELIGIBILITY_OPERAND_DICTIONARY_TYPE =
  'ELIGIBILITY_CRITERIA_OPERAND';

/**
 * Local order + fallback labels for the Eligibility "Dimension" dropdown; the
 * dictionary `description` wins as the label (see `buildDictionaryOptions`).
 */
export const ELIGIBILITY_CRITERIA_OPTIONS = [
  { value: EligibilityCriteriaDtoCriteria.AGI, label: 'AGI' },
  { value: EligibilityCriteriaDtoCriteria.GPA_RAW, label: 'GPA' },
  { value: EligibilityCriteriaDtoCriteria.SAI, label: 'SAI' },
  { value: EligibilityCriteriaDtoCriteria.PELL, label: 'Pell Amount' },
  { value: EligibilityCriteriaDtoCriteria.CEEB, label: 'CEEB code list' },
  { value: EligibilityCriteriaDtoCriteria.DISTANCE, label: 'Distance' },
  { value: EligibilityCriteriaDtoCriteria.APP_DATE, label: 'App Date' },
] as const satisfies ReadonlyArray<
  CalculationOption<EligibilityCriteriaDtoCriteria>
>;

/**
 * Distance and App Date are inherently ranges with no operator choice — the
 * Condition dropdown and single Value field are replaced by required From/To
 * fields (design doc §2, FR-1).
 */
export const ELIGIBILITY_RANGE_ONLY_CRITERIA: ReadonlySet<EligibilityCriteriaDtoCriteria> =
  new Set([
    EligibilityCriteriaDtoCriteria.DISTANCE,
    EligibilityCriteriaDtoCriteria.APP_DATE,
  ]);

/**
 * Local order + labels for the Eligibility "Condition" dropdown, pinned to
 * math symbols by the Figma design (node 10768-37756) — same symbols in the
 * dropdown and in the DETAILS table chip. `IN` is deliberately absent: it is
 * CEEB-only and applied server-side
 * (`AidModelCalculationService.normalizeEligibilityCriterias`), so it is never
 * user-selectable.
 */
export const ELIGIBILITY_OPERAND_OPTIONS = [
  { value: EligibilityCriteriaDtoOperand.EQUAL, label: '=' },
  { value: EligibilityCriteriaDtoOperand.NOT_EQUAL, label: '≠' },
  { value: EligibilityCriteriaDtoOperand.GREATER, label: '>' },
  { value: EligibilityCriteriaDtoOperand.AT_LEAST, label: '≥' },
  { value: EligibilityCriteriaDtoOperand.LESS, label: '<' },
  { value: EligibilityCriteriaDtoOperand.AT_MOST, label: '≤' },
  { value: EligibilityCriteriaDtoOperand.BETWEEN, label: 'Between' },
] as const satisfies ReadonlyArray<
  CalculationOption<EligibilityCriteriaDtoOperand>
>;

/**
 * These symbols are pinned by design, not by the dictionary's own wording
 * ("Equal to", "At least", …), so the local label always wins for every
 * operand.
 */
export const ELIGIBILITY_OPERAND_LOCAL_LABELS: ReadonlySet<EligibilityCriteriaDtoOperand> =
  new Set([
    EligibilityCriteriaDtoOperand.EQUAL,
    EligibilityCriteriaDtoOperand.NOT_EQUAL,
    EligibilityCriteriaDtoOperand.GREATER,
    EligibilityCriteriaDtoOperand.AT_LEAST,
    EligibilityCriteriaDtoOperand.LESS,
    EligibilityCriteriaDtoOperand.AT_MOST,
    EligibilityCriteriaDtoOperand.BETWEEN,
  ]);

/** Eligibility dimensions whose value is a dollar amount (rendered with a `$` adornment). */
export const ELIGIBILITY_CURRENCY_CRITERIA: ReadonlySet<EligibilityCriteriaDtoCriteria> =
  new Set([
    EligibilityCriteriaDtoCriteria.AGI,
    EligibilityCriteriaDtoCriteria.PELL,
  ]);

/** Field label for the CEEB codes chip input (FR-2). */
export const CEEB_CODES_LABEL = 'CEEB Codes';

/** Helper text under the CEEB codes label (FR-3) — does not commit to a digit count. */
export const CEEB_CODES_HELPER_TEXT =
  'Enter CEEB (College Board) codes exactly as issued';

/** FR-21: shown below the chip area whenever ≥1 (but not all) chips are unmatched. */
export const CEEB_REMOVE_ALL_UNMATCHED_LABEL = 'Remove all unmatched';

/** Mirrors `CalculationRuleFilter.DESCRIPTION_MAX_LENGTH` on the backend. */
export const SPECIAL_AWARD_DESCRIPTION_MAX_LENGTH = 150;

export const VARIANT_SECTION_VISIBILITY: Record<
  CalculationVariant,
  CalculationSectionVisibility
> = {
  percentOfNeed: {
    giftTarget: true,
    includeRequiredFees: true,
    loansWorkStudy: true,
    calculateNewNeed: true,
    grantCap: true,
  },
  agiSai: {
    giftTarget: true,
    includeRequiredFees: false,
    loansWorkStudy: false,
    calculateNewNeed: false,
    grantCap: true,
  },
  flat: {
    giftTarget: false,
    includeRequiredFees: false,
    loansWorkStudy: false,
    calculateNewNeed: true,
    grantCap: true,
  },
};
